import React from "react"
import { BodyText, InlineText, SubTitle } from "../../Typography/Typography"
import SmartLink from "../../layout/SmartLink/SmartLink"

const providerProfileClickEvent = () => ({
  name: "SEO Filtered Directory - Copy link to provider clicked",
  properties: {
    "Care Type": "care_home",
    "GEO Location": "Cambridge",
    "Current URL":
      "https://www.caresourcer.com/s/providers/towns/cambridge/care-home/",
  },
})

export default {
  summary: (
    <BodyText marginBottom>
      Choosing a care home for a loved one can be difficult, however with 32
      care homes in Cambridge, of which 17 offer specialist dementia care, there
      should be something perfect for your loved one. Here are a few of our top
      tips to help you narrow your options and find the right care home.
    </BodyText>
  ),
  content: [
    <SubTitle marginBottom bold>
      Location is key
    </SubTitle>,
    <BodyText marginBottom>
      Not being able to regularly see friends and family is a common worry of
      people moving into a care home, so take time to consider where visitors
      will be coming from and ensure the home you select has good transport
      links. As Cambridge is not a large city, within a 10min drive from the
      city center you could choose from a centrally located home like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-3289009999/winston-house"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Winston House</InlineText>
      </SmartLink>{" "}
      which is opposite the Coventry Botanic Gardens, or venture out of the city
      to{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-109009556/symonds-house"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Symonds House</InlineText>
      </SmartLink>{" "}
      which is surrounded by beautiful countryside in a small village called
      Linton. Cambridge offers lots of options to find the perfect home for your
      loved one.
    </BodyText>,
    <SubTitle marginBottom bold>
      Feeling at home
    </SubTitle>,
    <BodyText marginBottom>
      As well as thinking about how friends and family will visit, or where in
      Cambridge your loved one will feel most at home, it is important to
      consider their lifestyle and what would help make them feel most at home.
      Below are a few things to consider that will help make this change as
      smooth as possible for your loved one:
    </BodyText>,
    <SubTitle marginBottom bold italic>
      Gardens and outdoor spaces
    </SubTitle>,
    <BodyText marginBottom>
      Cambridge is known for having some lovely warm summers. Does your loved
      one like nothing more than being able to sit outside and bask in the heat,
      or do they prefer to stay indoors where it is cool? Understanding how
      important having an outdoor space is to your loved one should help you
      narrow down your options. Some care homes in Cambridge have little outdoor
      space, whilst others like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-125856119/hilton-park-care-centre"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Hilton Park Care Centre</InlineText>
      </SmartLink>{" "}
      have extensive grounds offering residents a countryside feeling where they
      are able to help garden or even tend to the chickens.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Pets welcome
    </BodyText>,
    <BodyText marginBottom>
      The UK is known for their love of pets and Cambridge is no different. It
      can be worrying when moving into a care home and having to leave you pets
      behind, however did you know that some care homes in Cambridge will let
      you move your pet in also? Many others allow pets to visit residents
      frequently, and others like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-130120291/cottenham-court-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Cottenham Court Care Home</InlineText>
      </SmartLink>
      , offer their residents frequent pet therapy visits. So if pets are
      important to your loved one, make sure you ask care homes about their pet
      policy.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Activities and days out
    </BodyText>,
    <BodyText marginBottom>
      It is important for residents to remain active when they move into a care
      home, and therefore most will offer a wide range of facilities such as
      cafes, on-site hair salons, music rooms and quiet spaces for relaxing. In
      Cambridge many homes also try to offer residents days out to places like
      Kettle&apos;s Yard, Grantchester or even to the local bar - when possible
      of course! Care homes are generally very accommodating so if there is
      something your loved one likes to do, ensure you ask if it would be
      possible when staying at the home.
    </BodyText>,
  ],
}
